/**
 * Adresse postale française, telle qu'elle est stockée au profil.
 *
 * Découpée en champs saisis séparément : la BAN propose un libellé unique, on
 * le répartit ensuite pour que code postal et commune restent éditables à part.
 */
export interface PostalAddress {
  readonly street: string;
  readonly postalCode: string;
  readonly commune: string;
}

export interface AddressSuggestion extends PostalAddress {
  /** Libellé complet affiché dans la liste de suggestions. */
  readonly label: string;
}

/** Propriétés utiles d'une Feature renvoyée par l'API Adresse. */
export interface BanFeatureProperties {
  readonly label?: string;
  readonly name?: string;
  readonly housenumber?: string;
  readonly street?: string;
  readonly postcode?: string;
  readonly city?: string;
}

/**
 * Transforme une Feature BAN en adresse Champ'Table.
 *
 * `name` contient déjà le numéro quand il est connu (« 12 Rue du Marché ») ;
 * sinon on reconstitue à partir de housenumber + street. Sans commune ni code
 * postal, la suggestion n'est pas exploitable pour le profil.
 */
export function addressFromBan(properties: BanFeatureProperties): AddressSuggestion | undefined {
  const postalCode = properties.postcode?.trim() ?? '';
  const commune = properties.city?.trim() ?? '';
  if (postalCode.length === 0 || commune.length === 0) {
    return undefined;
  }

  const street =
    properties.name?.trim() ||
    [properties.housenumber, properties.street].filter(Boolean).join(' ').trim();
  if (street.length === 0) {
    return undefined;
  }

  return {
    label: properties.label?.trim() || `${street} ${postalCode} ${commune}`,
    street,
    postalCode,
    commune,
  };
}
